/**
 * usePackingStats - Packing station statistics
 *
 * Fetches packing statistics for the current location:
 * - packedToday: Number of orders packed today
 * - binsClearedToday: Number of bins cleared today
 *
 * Auto-refreshes every 30 seconds to show current counts.
 * Uses skipToken pattern for conditional queries.
 *
 * @param locationId - The location ID to fetch stats for, or null
 * @returns TanStack Query result with packing stats
 */
import { trpc } from '@/lib/trpc';
import { skipToken } from '@tanstack/react-query';

export function usePackingStats(locationId: string | null) {
  return trpc.packing.getPackingStats.useQuery(
    locationId ? { locationId } : skipToken,
    {
      staleTime: 30_000,               // Consider fresh for 30s
      refetchInterval: 30_000,         // Auto-refresh every 30s
      refetchIntervalInBackground: false, // Pause when tab hidden
      refetchOnWindowFocus: true,
    }
  );
}

/**
 * useRecentlyPacked - Recently packed/shipped orders
 *
 * Fetches up to 10 most recently packed orders for the current location.
 * Shows in idle state below stats cards for quick visibility.
 *
 * @param locationId - The location ID to fetch for, or null
 * @returns TanStack Query result with recently packed orders
 */
export function useRecentlyPacked(locationId: string | null) {
  return trpc.packing.getRecentlyPacked.useQuery(
    locationId ? { locationId } : skipToken,
    {
      staleTime: 15_000,               // Consider fresh for 15s
      refetchInterval: 30_000,         // Auto-refresh every 30s
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
    }
  );
}
